// A sound is a list of layers of four kinds: ring, knock, sweep, and air.
// `renderLayers` adds them into one buffer of samples. Pure. The method is
// the one of the BopBox edit sounds, `docs/MADE_SOUNDS.md` in that repo.

import { SOUND_FADE_SECONDS, SOUND_SAMPLE_RATE } from '../constants';

const TAU = Math.PI * 2;
/** A partial over this share of the sample rate is left out. */
const PARTIAL_CEILING = 0.45;
/** The share of the octave over each sweep. */
const SWEEP_OCTAVE_SHARE = 0.12;
/** The band of the air does not go over this, in hertz. */
const AIR_CEILING_HZ = 6500;
/** The buffer is this much longer than its last layer, in seconds. */
const TAIL_SECONDS = 0.01;

/** The ratio to the note, the share of the level, and the half-life in seconds. */
export type RingPartial = readonly [ratio: number, share: number, halfLife: number];

/** Partials that start together and die. */
export type RingLayer = {
  kind: 'ring';
  at: number;
  gain: number;
  seconds: number;
  hz: number;
  attack: number;
  partials: readonly RingPartial[];
};

/** A burst of noise that is gone in milliseconds: the strike. */
export type KnockLayer = {
  kind: 'knock';
  at: number;
  gain: number;
  seconds: number;
  halfLife: number;
  seed: number;
};

/** One sine whose pitch comes to its target: a creak, a thing that bends. */
export type SweepLayer = {
  kind: 'sweep';
  at: number;
  gain: number;
  seconds: number;
  from: number;
  to: number;
  glide: number;
  halfLife: number;
  attack: number;
  /** A slow wobble of the pitch: its rate in hertz and its depth. Null for none. */
  wobble: readonly [hz: number, depth: number] | null;
};

/** Noise through a band that moves: paper, a slide, a breath. */
export type AirLayer = {
  kind: 'air';
  at: number;
  gain: number;
  seconds: number;
  from: number;
  to: number;
  damping: number;
  halfLife: number;
  attack: number;
  seed: number;
};

export type Layer = RingLayer | KnockLayer | SweepLayer | AirLayer;

/** Seeded noise, from -1 to 1. The same seed gives the same sound on every load. */
const noise = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return (((t ^ (t >>> 14)) >>> 0) / 4294967296) * 2 - 1;
  };
};

const span = (layer: Layer): { start: number; count: number } => ({
  start: Math.floor(layer.at * SOUND_SAMPLE_RATE),
  count: Math.floor(layer.seconds * SOUND_SAMPLE_RATE),
});

const decay = (seconds: number, halfLife: number): number => 0.5 ** (seconds / halfLife);

const addRing = (out: Float32Array, layer: RingLayer): void => {
  const { start, count } = span(layer);
  const attack = Math.max(layer.attack * SOUND_SAMPLE_RATE, 1);
  const heard = layer.partials.filter(
    ([ratio]) => layer.hz * ratio < SOUND_SAMPLE_RATE * PARTIAL_CEILING,
  );
  for (let i = 0; i < count; i++) {
    const t = i / SOUND_SAMPLE_RATE;
    let value = 0;
    for (const [ratio, share, halfLife] of heard) {
      value += share * Math.sin(TAU * layer.hz * ratio * t) * decay(t, halfLife);
    }
    out[start + i] = (out[start + i] ?? 0) + value * layer.gain * Math.min(i / attack, 1);
  }
};

const addKnock = (out: Float32Array, layer: KnockLayer): void => {
  const { start, count } = span(layer);
  const grain = noise(layer.seed);
  for (let i = 0; i < count; i++) {
    const value = grain() * decay(i / SOUND_SAMPLE_RATE, layer.halfLife);
    out[start + i] = (out[start + i] ?? 0) + value * layer.gain;
  }
};

const addSweep = (out: Float32Array, layer: SweepLayer): void => {
  const { start, count } = span(layer);
  const attack = Math.max(layer.attack * SOUND_SAMPLE_RATE, 1);
  let phase = 0;
  for (let i = 0; i < count; i++) {
    const t = i / SOUND_SAMPLE_RATE;
    let hz = layer.to + (layer.from - layer.to) * decay(t, layer.glide);
    if (layer.wobble) hz *= 1 + layer.wobble[1] * Math.sin(TAU * layer.wobble[0] * t);
    phase += (TAU * hz) / SOUND_SAMPLE_RATE;
    const value = Math.sin(phase) + SWEEP_OCTAVE_SHARE * Math.sin(phase * 2);
    out[start + i] =
      (out[start + i] ?? 0) +
      value * decay(t, layer.halfLife) * Math.min(i / attack, 1) * layer.gain;
  }
};

const addAir = (out: Float32Array, layer: AirLayer): void => {
  const { start, count } = span(layer);
  const attack = Math.max(layer.attack * SOUND_SAMPLE_RATE, 1);
  const grain = noise(layer.seed);
  let low = 0;
  let band = 0;
  for (let i = 0; i < count; i++) {
    const t = i / SOUND_SAMPLE_RATE;
    const centre = layer.from * (layer.to / layer.from) ** (t / layer.seconds);
    const f = 2 * Math.sin((Math.PI * Math.min(centre, AIR_CEILING_HZ)) / SOUND_SAMPLE_RATE);
    low += f * band;
    const high = grain() - low - layer.damping * band;
    band += f * high;
    out[start + i] =
      (out[start + i] ?? 0) +
      band * decay(t, layer.halfLife) * Math.min(i / attack, 1) * layer.gain;
  }
};

const addLayer = (out: Float32Array, layer: Layer): void => {
  if (layer.kind === 'ring') addRing(out, layer);
  else if (layer.kind === 'knock') addKnock(out, layer);
  else if (layer.kind === 'sweep') addSweep(out, layer);
  else addAir(out, layer);
};

/** The samples of the layers together, with the loudest sample at `level`. */
export const renderLayers = (layers: readonly Layer[], level: number): Float32Array => {
  const end = Math.max(0, ...layers.map((layer) => layer.at + layer.seconds));
  const out = new Float32Array(Math.ceil((end + TAIL_SECONDS) * SOUND_SAMPLE_RATE));
  for (const layer of layers) addLayer(out, layer);
  const peak = out.reduce((most, sample) => Math.max(most, Math.abs(sample)), 0);
  const gain = peak > 0 ? level / peak : 0;
  const fade = Math.floor(SOUND_FADE_SECONDS * SOUND_SAMPLE_RATE);
  const fadeFrom = out.length - fade;
  return out.map((sample, i) => sample * gain * (i > fadeFrom ? (out.length - i) / fade : 1));
};

// ---- Short forms for a table of layers ---------------------------------------

/** A knock lasts this many of its half-lives. */
const KNOCK_HALF_LIVES = 8;
/** A ring lasts this many of its longest half-life. */
const RING_HALF_LIVES = 8;
/** A sweep with no length lasts this many of its half-lives. */
const SWEEP_HALF_LIVES = 6;

export const knock = (at: number, gain: number, halfLife: number, seed: number): KnockLayer => ({
  kind: 'knock',
  at,
  gain,
  halfLife,
  seconds: halfLife * KNOCK_HALF_LIVES,
  seed,
});

export const ring = (
  hz: number,
  at: number,
  gain: number,
  partials: readonly RingPartial[],
): RingLayer => ({
  kind: 'ring',
  hz,
  at,
  gain,
  attack: 0.001,
  seconds: Math.max(...partials.map(([, , halfLife]) => halfLife)) * RING_HALF_LIVES,
  partials,
});

export type SweepShape = {
  glide?: number;
  halfLife?: number;
  seconds?: number;
  attack?: number;
  wobble?: readonly [hz: number, depth: number];
};

export const sweep = (
  from: number,
  to: number,
  at: number,
  gain: number,
  shape: SweepShape = {},
): SweepLayer => {
  const halfLife = shape.halfLife ?? 0.03;
  return {
    kind: 'sweep',
    from,
    to,
    at,
    gain,
    glide: shape.glide ?? 0.012,
    halfLife,
    seconds: shape.seconds ?? halfLife * SWEEP_HALF_LIVES,
    attack: shape.attack ?? 0.002,
    wobble: shape.wobble ?? null,
  };
};

export const air = (
  from: number,
  to: number,
  seconds: number,
  at: number,
  gain: number,
  halfLife = seconds * 0.5,
): AirLayer => ({
  kind: 'air',
  from,
  to,
  seconds,
  at,
  gain,
  damping: 0.9,
  halfLife,
  attack: seconds * 0.2,
  seed: 7,
});
